import { Routes } from '@angular/router';
import { provideStates } from '@ngxs/store';
import { OutputAnalyticsPageState } from '@portal-client/output-analytics/services/output-analytics-page-data.service';
import { ProjectBranchesPageState } from '@portal-client/projects/project-branches/services/project-branches-page-data.service';
import { ProjectBuildsPageState } from '@portal-client/projects/project-builds/services/project-builds-page-data.service';
import { ProjectChecklistsPageState } from '@portal-client/projects/project-checklists/services/project-checklists-page-data.service';
import { ProjectCommitsPageState } from '@portal-client/projects/project-commits/services/project-commits-page-data.service';
import { ProjectReportsPageState } from '@portal-client/projects/project-reports/services/project-reports-page-data.service';
import { ProjectTranslationPageState } from '@portal-client/projects/project-translation/services/project-translation-page-data.service';
import { ProjectPageState } from '@portal-client/projects/project/services/project-page-data.service';
import { ProjectFilesState } from '@portal-core/project-files/services/project-files-data.service';
import { AppNavbarLoggedOutLaunchPadComponent } from '@env/app/app/components/app-navbar-logged-out-launch-pad/app-navbar-logged-out-launch-pad.component';
import { AppFullNavbarHeadingComponent } from '@portal-client/app/components/app-full-navbar-heading/app-full-navbar-heading.component';
import { AppNavComponent } from '@portal-client/app/components/app-nav/app-nav.component';
import { AppNavbarUserLaunchPadComponent } from '@portal-client/app/components/app-navbar-user-launch-pad/app-navbar-user-launch-pad.component';
import { AppShellComponent } from '@portal-client/app/components/app-shell/app-shell.component';
import { licenseDefaultPageResolver } from '@portal-client/core/auth/license-default-page.resolver';
import { authLicenseUserDataGuard } from '@portal-client/core/guards/auth-license-user-data.guard';
import { authGuard } from '@portal-core/auth/guards/auth.guard';
import { browserHostVersionGuard } from '@portal-core/auth/guards/browser-host-version.guard';
import { centralInstanceSubdomainGuard } from '@portal-core/auth/guards/central-instance-subdomain.guard';
import { pageNotFoundGuard } from '@portal-core/auth/guards/page-not-found.guard';
import { defaultPageResolver } from '@portal-core/auth/resolvers/default-page.resolver';
import { flareLoginEmitterResolver } from '@portal-core/auth/resolvers/flare-login-emitter.resolver';
import { dataStreamInitResolver } from '@portal-core/data-stream/resolvers/data-stream-init.resolver';
import { EmptyComponent } from '@portal-core/general/components/empty/empty.component';
import { AlertType } from '@portal-core/general/enums/alert-type.enum';
import { FullPageComponent } from '@portal-core/layout/components/full-page/full-page.component';
import { authorAccessResolver } from '@portal-core/user-access/services/author-access.resolver';
import { authorOrSmeAccessResolver } from '@portal-core/user-access/services/author-or-sme-access.resolver';

export const appRoutes: Routes = [
  /** This route is used when the subdomain is invalid. It is at the root because it does not require a valid subdomain. */
  {
    path: 'licenseNotFound',
    data: {
      type: AlertType.LicenseNotFound
    },
    component: FullPageComponent,
    children: [
      {
        path: '',
        outlet: 'full-navbar-heading',
        component: AppFullNavbarHeadingComponent
      },
      {
        path: '',
        loadChildren: () => import('../alert/alert.routes').then(m => m.ALERT_ROUTES)
      }
    ]
  },
  /** The root route for the rest of the pages in the app. */
  {
    path: '',
    canActivate: [browserHostVersionGuard],
    children: [
      {
        path: '',
        canActivate: [centralInstanceSubdomainGuard],
        resolve: {
          dataStream: dataStreamInitResolver
        },
        children: [
          /**
           * This route is used to logout a user and does not require the auto-login guard or auth-user-data resolver.
           * Both of which can redirect the user if there is an auth error.
           * We don't want redirects to happen.
           * We just want to get the user fully logged out and back to square one.
           */
          {
            path: 'logout',
            component: FullPageComponent,
            children: [
              {
                path: '',
                outlet: 'full-navbar-heading',
                component: AppFullNavbarHeadingComponent
              },
              {
                path: '',
                loadChildren: () => import('../logout/logout.routes').then(m => m.LOGOUT_ROUTES)
              }
            ]
          },
          {
            path: 'alert',
            component: FullPageComponent,
            children: [
              {
                path: '',
                outlet: 'full-navbar-heading',
                component: AppFullNavbarHeadingComponent
              },
              {
                path: '',
                loadChildren: () => import('../alert/alert.routes').then(m => m.ALERT_ROUTES)
              }
            ]
          },
          {
            path: '',
            canActivate: [authLicenseUserDataGuard],
            // Always run the guard so that the auth user data gets reloaded when the user logs in
            runGuardsAndResolvers: 'always',
            children: [
              /* Anonymous Routes */
              {
                path: 'login',
                component: FullPageComponent,
                children: [
                  {
                    path: '',
                    children: [
                      {
                        path: '',
                        outlet: 'full-navbar-heading',
                        component: AppFullNavbarHeadingComponent
                      },
                      {
                        path: '',
                        loadChildren: () => import('../login/login.routes').then(m => m.LOGIN_ROUTES)
                      }
                    ]
                  }
                ]
              },
              {
                path: 'forgot-password',
                component: FullPageComponent,
                children: [
                  {
                    path: '',
                    outlet: 'full-navbar-heading',
                    component: AppFullNavbarHeadingComponent
                  },
                  {
                    path: '',
                    loadChildren: () => import('../forgot-password/forgot-password.routes').then(m => m.FORGOT_PASSWORD_ROUTES)
                  }
                ]
              },
              {
                path: 'reset-password',
                component: FullPageComponent,
                children: [
                  {
                    path: '',
                    outlet: 'full-navbar-heading',
                    component: AppFullNavbarHeadingComponent
                  },
                  {
                    path: '',
                    loadChildren: () => import('../reset-password/reset-password.routes').then(m => m.RESET_PASSWORD_ROUTES)
                  }
                ]
              },
              {
                path: 'expired-password',
                component: FullPageComponent,
                children: [
                  {
                    path: '',
                    outlet: 'full-navbar-heading',
                    component: AppFullNavbarHeadingComponent
                  },
                  {
                    path: '',
                    loadChildren: () => import('../expired-password/expired-password.routes').then(m => m.EXPIRED_PASSWORD_ROUTES)
                  }
                ]
              },
              {
                path: 'confirm',
                component: FullPageComponent,
                children: [
                  {
                    path: '',
                    outlet: 'full-navbar-heading',
                    component: AppFullNavbarHeadingComponent
                  },
                  {
                    path: '',
                    outlet: 'app-navbar-launch-pad',
                    component: AppNavbarLoggedOutLaunchPadComponent
                  },
                  {
                    path: '',
                    children: [
                      {
                        path: 'handoff',
                        loadComponent: () => import('../confirm/handoff/components/handoff-page/handoff-page.component').then(m => m.HandoffPageComponent)
                      },
                      {
                        path: 'sso',
                        loadComponent: () => import('../confirm/sso/components/sso-page/sso-page.component').then(m => m.SsoPageComponent)
                      },
                      {
                        path: 'sso-email',
                        loadChildren: () => import('../confirm/new-sso-user/new-sso-user.routes').then(m => m.NEW_SSO_USER_ROUTES)
                      },
                      {
                        path: '',
                        loadChildren: () => import('../confirm/new-user/new-user.routes').then(m => m.NEW_USER_ROUTES)
                      }
                    ]
                  }
                ]
              },
              /* Authenticated Routes */
              {
                path: ':licenseId',
                canActivate: [authGuard],
                resolve: {
                  loginEmitter: flareLoginEmitterResolver
                },
                data: {
                  hasLicenseId: true,
                  requiresAuth: true
                },
                children: [
                  {
                    path: 'confirm',
                    component: FullPageComponent,
                    children: [
                      {
                        path: '',
                        outlet: 'full-navbar-heading',
                        component: AppFullNavbarHeadingComponent
                      },
                      {
                        path: '',
                        outlet: 'app-navbar-launch-pad',
                        component: AppNavbarUserLaunchPadComponent
                      },
                      {
                        path: '',
                        children: [
                          {
                            path: 'slack',
                            loadComponent: () => import('../confirm/slack/components/slack-page/slack-page.component').then(m => m.SlackPageComponent),
                            resolve: {
                              allowedAccess: authorAccessResolver
                            }
                          }
                        ]
                      }
                    ]
                  },
                  {
                    // Single-outlet persistent shell (Phase 3). Every authenticated feature lives
                    // here as a mc-page child; the former SubPageFillComponent + named-outlet shell
                    // has been fully retired.
                    path: '',
                    component: AppShellComponent,
                    children: [
                      {
                        path: 'teams',
                        loadComponent: () => import('../teams/components/teams-page/teams-page.component').then(m => m.TeamsPageComponent),
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        path: 'users',
                        loadComponent: () => import('../users/components/users-page/users-page.component').then(m => m.UsersPageComponent),
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        path: 'home',
                        loadComponent: () => import('../home/components/home-page/home-page.component').then(m => m.HomePageComponent),
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        path: 'sites',
                        loadChildren: () => import('../sites/sites.routes').then(m => m.SITES_ROUTES),
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        path: 'reviews',
                        loadChildren: () => import('../reviews/reviews.routes').then(m => m.REVIEWS_ROUTES),
                        resolve: {
                          allowedAccess: authorOrSmeAccessResolver
                        }
                      },
                      {
                        path: 'analytics',
                        loadChildren: () => import('../output-analytics/output-analytics.routes').then(m => m.OUTPUT_ANALYTICS_ROUTES),
                        // The former OutputAnalyticsModule registered this state via NgxsModule.forFeature;
                        // provideStates here keeps it lazily scoped to the analytics feature.
                        providers: [provideStates([OutputAnalyticsPageState])],
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        path: 'tasks',
                        loadChildren: () => import('../tasks/tasks.routes').then(m => m.TASKS_ROUTES),
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        path: 'projects',
                        loadChildren: () => import('../projects/projects.routes').then(m => m.PROJECTS_ROUTES),
                        // The former ProjectsModule registered these page states via NgxsModule.forFeature;
                        // provideStates here keeps them lazily scoped to the projects feature.
                        providers: [
                          provideStates([
                            ProjectBuildsPageState,
                            ProjectChecklistsPageState,
                            ProjectCommitsPageState,
                            ProjectFilesState,
                            ProjectPageState,
                            ProjectReportsPageState,
                            ProjectTranslationPageState,
                            ProjectBranchesPageState
                          ])
                        ],
                        resolve: {
                          allowedAccess: authorAccessResolver
                        }
                      },
                      {
                        // Default landing for the bare /:licenseId route (formerly served by the
                        // retired SubPageFillComponent shell): the resolver navigates to the license
                        // user's home (author) or reviews (SME) page. Rendered inside AppShellComponent
                        // so the persistent shell stays mounted through the redirect.
                        path: '',
                        pathMatch: 'full',
                        resolve: {
                          defaultPage: licenseDefaultPageResolver
                        },
                        component: EmptyComponent
                      }
                    ]
                  }
                ]
              },
              /* empty route */
              {
                path: '',
                resolve: {
                  defaultPage: defaultPageResolver,
                },
                component: EmptyComponent
              },
              /* No matching routes */
              {
                path: '**',
                canActivate: [pageNotFoundGuard],
                component: AppNavComponent
              }
            ]
          }
        ]
      }
    ]
  }
];
